import { ActionType, BallAction, FrameStencil, HandoffAction, PlayerAction, PossessionAction } from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestHandoff, findClosestPath, findClosestStencil } from "../find-closest";
import { frameStencilController } from "../frame-stencil-controller";
import {
  getBallActions,
  getHandoffsNoNextAction,
  getNonAssociatedPlayerAndPossessionActions,
  getPlayerActionsNoNext,
  getPlayerMarkers,
  getPlayerMarkersNoMovement,
  getPossessionActions,
  getPossessionActionsNoNextPlayer,
  getReceivingHandoffLayerType,
} from "../frame-stencil-filters";
import { DEFAULT_PASS_DURATION } from "../get-duration-from-action";
import { refreshMovements } from "../refresh-movements";
import { checkActionEndPoint } from "./check-action-end-point";
import { checkPlayerPossessionActionStartPoint } from "./check-player-possession-action-start-point";
import { getHandoffStartTimes } from "./get-handoff-start-times";

export interface AddPlayerActionProps {
  action: Omit<PlayerAction, "startTime" | "endTime">;
  stencils: FrameStencil[];
}

export const addPlayerAction = ({ action, stencils }: AddPlayerActionProps): FrameStencil[] => {
  // get all players that don't have a movement and playerActions that don't have a nextPlayerAction
  const playersAndPlayerActionsNoNext = [
    ...getPlayerMarkersNoMovement(stencils),
    ...getPlayerActionsNoNext(stencils),
    ...getPossessionActionsNoNextPlayer(stencils),
  ];

  const { action: actionWithTimes, stencils: stencilsWithUpdatedPrevStencil } = checkPlayerPossessionActionStartPoint({
    stencils,
    stencilsToCheck: playersAndPlayerActionsNoNext,
    action,
  });

  const { next, newStencils } = checkActionEndPoint({
    action: actionWithTimes,
    actionsToCheck: getNonAssociatedPlayerAndPossessionActions(stencilsWithUpdatedPrevStencil),
    stencils: stencilsWithUpdatedPrevStencil,
  });

  /** add closestAction to nextPlayerAction */
  const actionWithNext: PlayerAction = { ...actionWithTimes, nextPlayerAction: next?.id || null };

  /** get all ballActions that don't have a playerActionEnd
   * find closest to endPoint */
  const passesWithoutPlayerActionEnd = getBallActions(newStencils).filter((b) => !b.playerActionEnd);
  const closestPassToEndPoint = findClosestStencil({
    target: { id: action.id, point: action.endPos },
    stencils: passesWithoutPlayerActionEnd,
    stencilsPointAccessor: (s) => s.endPos,
  });

  const playerOnNewAction = getPlayerMarkers(refreshMovements([...newStencils, actionWithNext])).find((s) =>
    s.movements.map((m) => m.id).includes(action.id)
  );

  const updatedClosestPassToEndPoint: BallAction | null = closestPassToEndPoint
    ? {
        ...closestPassToEndPoint,
        endTime: Math.max(closestPassToEndPoint.endTime, actionWithNext.endTime),
        startTime: Math.max(closestPassToEndPoint.endTime, actionWithNext.endTime) - DEFAULT_PASS_DURATION,
        ...(playerOnNewAction && { nextPlayer: playerOnNewAction.id }),
      }
    : null;

  const stencilsWithUpdatedPassEnd = updatedClosestPassToEndPoint
    ? sliceItemUpdate({ item: { ...updatedClosestPassToEndPoint, playerActionEnd: action.id }, arr: newStencils })
    : newStencils;

  const stencilsWithNewPlayerAction = refreshMovements([...stencilsWithUpdatedPassEnd, actionWithNext]);

  const stencilsWithHandoffUpdates = ((): FrameStencil[] => {
    const closestHandoff = findClosestHandoff({
      stencils: getHandoffsNoNextAction(stencilsWithNewPlayerAction),
      target: actionWithNext,
    });

    if (!closestHandoff) return stencilsWithNewPlayerAction;

    const possessionIntersection = findClosestPath({
      stencils: getPossessionActions(stencilsWithNewPlayerAction).filter(
        (s) => s.id === closestHandoff.action.prevAction
      ),
      target: closestHandoff.action.pos,
    });

    const { possessionStartTime, playerStartTime, handoffStartTime } = getHandoffStartTimes({
      possessionIntersection,
      playerIntersection: { ...closestHandoff, action: actionWithNext },
      handoff: closestHandoff.action,
    });

    const updatedPossessionAction: PossessionAction | null = possessionIntersection
      ? {
          ...possessionIntersection.action,
          startTime: possessionStartTime,
          endTime:
            possessionStartTime + (possessionIntersection.action.endTime - possessionIntersection.action.startTime),
        }
      : null;

    const updatedPlayerAction: PossessionAction = {
      ...actionWithNext,
      actionType: ActionType.POSSESSION,
      layerType: getReceivingHandoffLayerType(actionWithNext.layerType),
      nextBallAction: null,
      startTime: playerStartTime,
      endTime: playerStartTime + (actionWithNext.endTime - actionWithNext.startTime),
    };

    const playerOnNewPlayerAction = getPlayerMarkers(stencilsWithNewPlayerAction).find((p) =>
      p.movements.some((m) => m.id === actionWithNext.id)
    );
    const updatedHandoffAction: HandoffAction = {
      ...closestHandoff.action,
      nextAction: actionWithNext.id,
      nextPlayer: playerOnNewPlayerAction?.id || null,
      endPos: closestHandoff.intersection,
      startTime: handoffStartTime,
      endTime: handoffStartTime + (closestHandoff.action.endTime - closestHandoff.action.startTime),
    };

    const stencilsWithUpdatedHandoff = [updatedPossessionAction, updatedHandoffAction]
      .filter((s): s is PossessionAction | HandoffAction => s !== null)
      .reduce((acc, s) => sliceItemUpdate({ item: s, arr: acc }), stencilsWithNewPlayerAction);

    return frameStencilController({
      stencils: stencilsWithUpdatedHandoff,
      operation: "UPDATE",
      stencil: updatedPlayerAction,
    });
  })();

  return refreshMovements(stencilsWithHandoffUpdates);
};
